import type { Money } from '@pf/contracts';

/**
 * Money rendering and comparison, done entirely on decimal strings.
 *
 * SAD §8: "Money is decimal strings + currency (never float)." `docs/plan/06-risks.md` R3 turns
 * that into a lint rule banning `parseFloat`. This module is the console's answer to the
 * question that rule raises — *how do you render and compare an amount without ever making it a
 * `number`?* — and the answer is `BigInt` over the scaled integer representation. Every value
 * that reaches a pixel goes through here.
 *
 * The invariant this module protects: what the operator reads on screen is byte-for-byte the
 * amount the API sent, re-grouped for legibility and nothing more. A `0.1 + 0.2` never happens
 * because no float ever exists.
 */

/** Minor-unit precision per currency. Mirrors `@pf/shared`'s `decimalPlacesFor`. */
const DECIMAL_PLACES: Readonly<Record<string, number>> = { THB: 2 };

/** Currency display symbols. Absent currencies fall back to the ISO code. */
const CURRENCY_SYMBOL: Readonly<Record<string, string>> = { THB: '฿' };

const DECIMAL_STRING = /^(\d+)(?:\.(\d+))?$/;

/** A decimal string decomposed into an exact scaled integer. */
export interface ScaledDecimal {
  /** The value multiplied by `10 ** scale`, exactly. */
  readonly units: bigint;
  /** Number of decimal places `units` is scaled by. */
  readonly scale: number;
}

/** Thrown when a value that must be a wire-format decimal string is not one. */
export class InvalidDecimalStringError extends Error {
  /** Build the error for `value`. */
  constructor(readonly value: string) {
    super(`"${value}" is not a non-negative decimal string`);
    this.name = 'InvalidDecimalStringError';
  }
}

/** Decimal places used when rendering `currency`. Unknown currencies render at 2. */
export function decimalPlacesFor(currency: string): number {
  return DECIMAL_PLACES[currency] ?? 2;
}

/** The display symbol for `currency`, or the ISO code when there is no symbol. */
export function symbolFor(currency: string): string {
  return CURRENCY_SYMBOL[currency] ?? currency;
}

/**
 * Decompose a non-negative decimal string into an exact scaled integer.
 *
 * @throws {@link InvalidDecimalStringError} when `value` is not `\d+(\.\d+)?`.
 */
export function parseDecimalString(value: string): ScaledDecimal {
  const match = DECIMAL_STRING.exec(value.trim());
  if (!match) throw new InvalidDecimalStringError(value);
  const whole = match[1] ?? '0';
  const fraction = match[2] ?? '';
  return { units: BigInt(whole + fraction), scale: fraction.length };
}

/** Re-scale `d` to exactly `scale` places. Widening is exact; narrowing truncates. */
export function rescale(d: ScaledDecimal, scale: number): ScaledDecimal {
  if (scale === d.scale) return d;
  if (scale > d.scale) {
    return { units: d.units * 10n ** BigInt(scale - d.scale), scale };
  }
  return { units: d.units / 10n ** BigInt(d.scale - scale), scale };
}

/** Render a {@link ScaledDecimal} back to a plain decimal string. */
export function toDecimalString(d: ScaledDecimal): string {
  if (d.scale === 0) return d.units.toString();
  const digits = d.units.toString().padStart(d.scale + 1, '0');
  const cut = digits.length - d.scale;
  return `${digits.slice(0, cut)}.${digits.slice(cut)}`;
}

/**
 * Exact three-way comparison of two decimal strings. Never approximate.
 *
 * @returns a negative number if `a < b`, `0` if exactly equal, a positive number if `a > b`.
 */
export function compareDecimalStrings(a: string, b: string): number {
  const left = parseDecimalString(a);
  const right = parseDecimalString(b);
  const scale = Math.max(left.scale, right.scale);
  const lu = rescale(left, scale).units;
  const ru = rescale(right, scale).units;
  return lu === ru ? 0 : lu < ru ? -1 : 1;
}

/** True when two decimal strings denote exactly the same value (`"5"` equals `"5.00"`). */
export function decimalStringsEqual(a: string, b: string): boolean {
  return compareDecimalStrings(a, b) === 0;
}

/** Exact sum of decimal strings, returned as a decimal string at the widest input scale. */
export function sumDecimalStrings(values: readonly string[]): string {
  if (values.length === 0) return '0';
  const parsed = values.map(parseDecimalString);
  const scale = parsed.reduce((max, d) => Math.max(max, d.scale), 0);
  const total = parsed.reduce((acc, d) => acc + rescale(d, scale).units, 0n);
  return toDecimalString({ units: total, scale });
}

/**
 * Exact absolute difference of two decimal strings, as a decimal string at the wider scale.
 *
 * Absolute rather than signed because {@link parseDecimalString} only accepts non-negative
 * values — every caller here is comparing two amounts and wants the gap between them, and a
 * negative decimal string is not something the wire format or this module can represent.
 */
export function differenceDecimalStrings(a: string, b: string): string {
  const left = parseDecimalString(a);
  const right = parseDecimalString(b);
  const scale = Math.max(left.scale, right.scale);
  const lu = rescale(left, scale).units;
  const ru = rescale(right, scale).units;
  return toDecimalString({ units: lu > ru ? lu - ru : ru - lu, scale });
}

/** Exact sum of {@link Money} values. All inputs must share a currency. */
export function sumMoney(values: readonly Money[]): Money {
  const first = values[0];
  if (!first) throw new Error('sumMoney requires at least one amount');
  for (const m of values) {
    if (m.currency !== first.currency) {
      // Read both codes out before the comparison narrows them to `never`.
      const [expected, actual]: [string, string] = [first.currency, m.currency];
      throw new Error(`cannot sum ${expected} with ${actual}`);
    }
  }
  return {
    value: sumDecimalStrings(values.map((m) => m.value)),
    currency: first.currency,
  };
}

/** Group the integer part of a decimal string with thousands separators. */
function groupThousands(whole: string, separator: string): string {
  let out = '';
  for (let i = 0; i < whole.length; i += 1) {
    const remaining = whole.length - i;
    out += whole[i] ?? '';
    if (remaining > 1 && remaining % 3 === 1) out += separator;
  }
  return out;
}

/** Options for {@link formatDecimalString} and {@link formatMoney}. */
export interface FormatMoneyOptions {
  /** Force a fixed number of decimal places. Defaults to the currency's minor units. */
  decimalPlaces?: number;
  /** Thousands separator. Defaults to `','`. */
  groupSeparator?: string;
  /** Prefix the currency symbol (`฿1,000.00`). Defaults to `false`. */
  withSymbol?: boolean;
  /** Append the ISO code (`1,000.00 THB`). Defaults to `false`. */
  withCode?: boolean;
}

/**
 * Format a decimal string for display: pad or truncate to the currency's scale, then group.
 *
 * Truncation, never rounding — showing a rounded-up amount next to an exact one is how an
 * operator ends up approving a transaction for a value the system does not hold.
 */
export function formatDecimalString(
  value: string,
  currency: string,
  options: FormatMoneyOptions = {},
): string {
  const places = options.decimalPlaces ?? decimalPlacesFor(currency);
  const separator = options.groupSeparator ?? ',';
  const scaled = rescale(parseDecimalString(value), places);
  const plain = toDecimalString(scaled);
  const dot = plain.indexOf('.');
  const whole = dot === -1 ? plain : plain.slice(0, dot);
  const fraction = dot === -1 ? '' : plain.slice(dot + 1);
  const grouped = groupThousands(whole, separator);
  const body = fraction ? `${grouped}.${fraction}` : grouped;
  const prefix = options.withSymbol ? symbolFor(currency) : '';
  const suffix = options.withCode ? ` ${currency}` : '';
  return `${prefix}${body}${suffix}`;
}

/** Format a {@link Money} for display. See {@link formatDecimalString}. */
export function formatMoney(money: Money, options: FormatMoneyOptions = {}): string {
  return formatDecimalString(money.value, money.currency, options);
}

/** Build a {@link Money} from a decimal string, validating its shape. */
export function money(value: string, currency: Money['currency'] = 'THB'): Money {
  parseDecimalString(value);
  return { value, currency };
}

/**
 * A short, human-scaled label for chart axes and stat tiles — `฿1.2M`, `฿480k`.
 *
 * The only place in the console where an amount is deliberately *not* exact. It is used for
 * axis ticks and headline aggregates only, never for a per-transaction amount, and the exact
 * value is always available in an adjacent tooltip or table cell.
 */
export function formatCompactAmount(value: string, currency: string): string {
  const { units, scale } = rescale(parseDecimalString(value), 0);
  const symbol = symbolFor(currency);
  const digits = units.toString().length;
  if (scale !== 0) throw new Error('unreachable: rescaled to 0');
  if (digits > 9) return `${symbol}${toDecimalString(rescale({ units, scale: 9 }, 1))}B`;
  if (digits > 6) return `${symbol}${toDecimalString(rescale({ units, scale: 6 }, 1))}M`;
  if (digits > 3) return `${symbol}${toDecimalString(rescale({ units, scale: 3 }, 1))}k`;
  return `${symbol}${units.toString()}`;
}
